"use client";

import { useRef, useState } from "react";
import Link from "next/link";
import { ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Field, describedBy, fieldClass, validators } from "./form-field";
import { useForm } from "./use-form";
import { TOPICS, type Topic } from "./contact-topics";

/**
 * Contact form. Client-side only: nothing is sent anywhere yet, so the success
 * state only promises what the team actually does (reply within a business day).
 */
export function ContactForm({ initialTopic = "" }: { initialTopic?: Topic | "" }) {
  const [sent, setSent] = useState(false);
  const doneRef = useRef<HTMLHeadingElement>(null);
  const form = useForm(
    { name: "", email: "", company: "", topic: initialTopic as string, message: "" },
    {
      name: validators.name,
      email: validators.email,
      topic: validators.topic,
      message: validators.message,
    },
    () => {
      setSent(true);
      requestAnimationFrame(() => doneRef.current?.focus());
    },
  );
  const { errors, bind, values } = form;

  if (sent) {
    return (
      <div role="status" className="flex min-h-[26rem] flex-col justify-center">
        <h2 ref={doneRef} tabIndex={-1} className="text-h3 text-navy">
          Thanks. We reply within one business day.
        </h2>
        <p className="mt-3 max-w-narrow text-pretty text-body text-slate">
          {values.topic === "Book a demo"
            ? "We will send a few times for a 30-minute demo to the address you gave us."
            : `We will reply to ${values.email.trim()}.`}
        </p>
        <div className="mt-8">
          <Button
            variant="secondary"
            onClick={() => {
              form.reset();
              setSent(false);
            }}
          >
            Send another message
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form noValidate onSubmit={form.onSubmit} aria-label="Contact Stirrup">
      <div className="grid gap-5 sm:grid-cols-2">
        <Field id="contact-name" label="Name" error={errors.name}>
          <input
            id="contact-name"
            type="text"
            autoComplete="name"
            className={fieldClass(!!errors.name)}
            aria-describedby={describedBy("contact-name", errors.name)}
            {...bind("name")}
          />
        </Field>
        <Field id="contact-email" label="Work email" error={errors.email}>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            className={fieldClass(!!errors.email)}
            aria-describedby={describedBy("contact-email", errors.email)}
            {...bind("email")}
          />
        </Field>
        <Field id="contact-company" label="Company" optional>
          <input
            id="contact-company"
            type="text"
            autoComplete="organization"
            className={fieldClass(false)}
            {...bind("company")}
          />
        </Field>
        <Field id="contact-topic" label="Topic" error={errors.topic}>
          <div className="relative">
            <select
              id="contact-topic"
              className={fieldClass(!!errors.topic, cn("appearance-none pr-10", !values.topic && "text-slate"))}
              aria-describedby={describedBy("contact-topic", errors.topic)}
              {...bind("topic")}
            >
              <option value="" disabled>
                Choose a topic
              </option>
              {TOPICS.map((t) => (
                <option key={t} value={t} className="text-navy">
                  {t}
                </option>
              ))}
            </select>
            <ChevronDown
              aria-hidden
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate"
            />
          </div>
        </Field>
        <Field
          id="contact-message"
          label="Message"
          hint="What you are working on, and what you would like from us."
          error={errors.message}
          className="sm:col-span-2"
        >
          <textarea
            id="contact-message"
            rows={5}
            className={fieldClass(!!errors.message, "resize-y")}
            aria-describedby={describedBy("contact-message", errors.message, true)}
            {...bind("message")}
          />
        </Field>
      </div>
      <div className="mt-7 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <Button type="submit" size="lg">
          Send message
        </Button>
        <p className="text-small text-slate">
          We use your details only to reply. See the{" "}
          <Link href="/privacy" className="prose-link">
            privacy policy
          </Link>
          .
        </p>
      </div>
    </form>
  );
}
